import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import { Search as SearchIcon, Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGlobalSearchQuery } from "state/api";
import useDebouncedValue from "hooks/useDebouncedValue";
import { isFinancialManager, isMediaBuyingOnly } from "utils/roles";
import { getPaletteActions } from "../../navConfig";
import { extras, light, fontStack } from "styles/tokens";
import { getRecents, pushRecent } from "./recentSearches";
import "./CommandPalette.css";

/**
 * Ctrl+K command palette — the app's one search surface. Opens from the
 * Navbar (search box / icon / Ctrl+K), searches leads by name, phone, or
 * email plus clients (role-aware on the server), and offers
 * every page the current role can visit (from navConfig, same source as the
 * Sidebar). Desktop: a top-anchored card. Mobile PWA: full screen.
 *
 * MUI Dialog supplies portal/focus-trap/Esc/scroll-lock; the content is
 * hand-rolled on design tokens per the ConfirmDialog pattern (white card in
 * both app modes). The body only mounts while open, so nothing fetches until
 * the user opens the palette and types (no fetch-on-mount, ever).
 */

// Token values injected as CSS custom properties (see CommandPalette.css).
const CSS_VARS = {
  "--cp-surface": light.surface,
  "--cp-text-high": light.textHigh,
  "--cp-text-mid": light.textMid,
  "--cp-text-low": light.textLow,
  "--cp-border": light.border,
  "--cp-border-strong": light.borderStrong,
  "--cp-radius": `${extras.radius.lg}px`,
  "--cp-shadow": extras.shadow.lg,
  "--cp-font": fontStack,
  "--cp-accent": extras.accent.main,
  "--cp-accent-subtle": extras.accent.subtle,
  "--cp-focus-ring": extras.shadow.focusRing,
};

const SECTION_TITLES = {
  lead: "Leads",
  client: "Clients",
};

const matchesPageAction = (action, needle) => {
  if (!needle) return true;
  const haystack = [action.label, action.group, ...(action.keywords || [])]
    .join(" ")
    .toLowerCase();
  return needle
    .toLowerCase()
    .split(/\s+/)
    .every((token) => haystack.includes(token));
};

function PaletteBody({ onClose, isNonMobile }) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);

  const trimmed = query.trim();
  const debounced = useDebouncedValue(trimmed, 200); // house-standard debounce
  // FM / media-buying roles have no entity search (the server 403s them);
  // their palette is page navigation only and never issues a request.
  const entitySearchEnabled =
    Boolean(user) && !isFinancialManager(user) && !isMediaBuyingOnly(user);
  const shouldSearch = entitySearchEnabled && debounced.length >= 2;
  const { data, isFetching } = useGlobalSearchQuery(
    { q: debounced },
    { skip: !shouldSearch }
  );
  // Only show server results that belong to what's currently typed —
  // otherwise the previous query's rows linger while the new one loads.
  // Compared lowercased: both the RTK and server caches are case-insensitive
  // (and the request lowercases q), so "Omar" retyped as "omar" must reuse
  // the cached payload instead of waiting forever for a fetch that never
  // comes. Arabic is unaffected by toLowerCase.
  const serverSections =
    shouldSearch && data && data.query === debounced.toLowerCase()
      ? data.sections
      : null;

  const pageActions = useMemo(() => getPaletteActions(user), [user]);
  const recents = useMemo(() => getRecents(), []);

  // One flat list of activatable rows, with section headers derived from
  // `group` transitions. activeIndex addresses this array.
  const rows = useMemo(() => {
    const out = [];
    if (!trimmed) {
      for (const recent of recents) {
        out.push({
          kind: "recent",
          group: "Recent",
          label: recent.label,
          sub: recent.sub || "",
          route: recent.route,
        });
      }
    }
    const filteredPages = pageActions.filter((a) =>
      matchesPageAction(a, trimmed)
    );
    for (const action of filteredPages) {
      out.push({
        kind: "page",
        group: "Pages",
        label: action.label,
        sub: action.group,
        route: action.path,
        Icon: action.Icon,
      });
    }
    if (serverSections) {
      for (const section of serverSections) {
        for (const item of section.items) {
          out.push({
            kind: section.type,
            group: SECTION_TITLES[section.type] || section.type,
            label: item.title,
            sub: item.snippet,
            matchedField: item.matchedField,
            route: item.routeHint,
            isEntity: true,
          });
        }
      }
    }
    return out;
  }, [trimmed, recents, pageActions, serverSections]);

  // Clamp/reset the active row whenever the list changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [trimmed, rows.length]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`#cp-opt-${activeIndex}`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  const activate = (row) => {
    if (!row) return;
    if (row.isEntity) {
      pushRecent({ label: row.label, sub: row.sub, route: row.route });
    }
    onClose();
    navigate(row.route);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (rows.length ? (i + 1) % rows.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) =>
        rows.length ? (i - 1 + rows.length) % rows.length : 0
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      activate(rows[activeIndex] || rows[0]);
    }
  };

  const showTooShortHint =
    entitySearchEnabled && trimmed.length === 1;
  const searching = shouldSearch && (isFetching || !serverSections);
  const noResults =
    trimmed.length >= 2 && rows.length === 0 && !searching;

  let renderedGroup = null;

  return (
    <div className="cp-root" style={CSS_VARS}>
      <div className="cp-header">
        <SearchIcon className="cp-header-icon" />
        <input
          className="cp-input"
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls="cp-listbox"
          aria-activedescendant={rows.length ? `cp-opt-${activeIndex}` : undefined}
          aria-label="Search the CRM"
          placeholder={
            entitySearchEnabled
              ? "Search leads by name, phone, or email…"
              : "Jump to a page…"
          }
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="go"
        />
        {isNonMobile ? (
          <span className="cp-esc-hint">esc</span>
        ) : (
          <button
            type="button"
            className="cp-close"
            aria-label="Close search"
            onClick={onClose}
          >
            <Close fontSize="small" />
          </button>
        )}
      </div>

      <div className="cp-list" id="cp-listbox" role="listbox" ref={listRef}>
        {rows.map((row, idx) => {
          const header =
            row.group !== renderedGroup ? (
              <div className="cp-group" key={`g-${row.group}`}>
                {row.group}
              </div>
            ) : null;
          renderedGroup = row.group;
          return (
            <React.Fragment key={`${row.kind}-${row.route}-${idx}`}>
              {header}
              <div
                id={`cp-opt-${idx}`}
                role="option"
                aria-selected={idx === activeIndex}
                className={`cp-row${idx === activeIndex ? " cp-row-active" : ""}`}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseDown={(e) => e.preventDefault()} /* keep input focus */
                onClick={() => activate(row)}
              >
                {row.Icon ? <row.Icon className="cp-row-icon" /> : null}
                <span className="cp-row-label">{row.label}</span>
                {row.sub ? <span className="cp-row-sub">{row.sub}</span> : null}
                {row.matchedField && row.matchedField !== "name" ? (
                  <span className="cp-row-chip">{row.matchedField}</span>
                ) : null}
              </div>
            </React.Fragment>
          );
        })}

        {showTooShortHint ? (
          <div className="cp-hint">Type at least 2 characters to search</div>
        ) : null}
        {searching ? <div className="cp-hint">Searching…</div> : null}
        {noResults ? <div className="cp-hint">No results</div> : null}
      </div>

      {isNonMobile ? (
        <div className="cp-footer">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      ) : null}
    </div>
  );
}

export default function CommandPalette({ open, onClose, isNonMobile }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={!isNonMobile}
      fullWidth
      maxWidth="sm"
      sx={
        isNonMobile
          ? { "& .MuiDialog-container": { alignItems: "flex-start" } }
          : undefined
      }
      PaperProps={{
        className: "cp-paper",
        sx: isNonMobile
          ? { mt: "10vh", borderRadius: `${extras.radius.lg}px`, overflow: "hidden" }
          : // Dark paper behind the dark .cp-root (CommandPalette.css) so
            // overscroll never flashes a white sheet on phones.
            { overflow: "hidden", backgroundColor: extras.surface.base },
      }}
    >
      {/* Body mounts only while open — its search hook can never fetch on
          app mount or in the background. */}
      {open ? <PaletteBody onClose={onClose} isNonMobile={isNonMobile} /> : null}
    </Dialog>
  );
}
